import React, { useCallback, useRef } from 'react';

import { Box, Flex, useTheme } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { SaveAndAddComponent } from 'components/RabbitOKRCreationPanel/OKRCreationPanelFooter';
import { useCreateNewOKRMutation } from 'components/RabbitOKRCreationPanel/datahooks/useCreateNewOKRMutation';
import { useOKRCreationValidation } from 'components/RabbitOKRCreationPanel/useOKRCreationValidation';
import { QUERY_KEYS } from 'components/RabbitOKRSidePanel/datahooks';
import { closeSecondaryPanel, openCreateOKRPanel } from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { useSelector, useDispatch } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { okrCreationPanelStateSelector } from 'selectors/okrCreationPanelSelector';

import { OKR_TYPES, TOAST_STATUSES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { sendOpenedKRPanelEvent, sendOpenedObjectivePanelEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { Button } from 'uiToolkitV1/Button';

const CreateOKRPanelFooter = ({ onCancel, onKeyResultCreation, onObjectiveCreation }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const toast = useToast();
  const page = useViewPageName();

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  const okrDetailsFromRedux = useSelector(okrCreationPanelStateSelector);
  const currentOpenOKRDetails = useSelector(kRDrawerSelector);
  const validationError = useOKRCreationValidation();

  const isObjective = okrDetailsFromRedux?.type === OKR_TYPES.OBJECTIVE;

  const okrDetailsRef = useRef();
  okrDetailsRef.current = okrDetailsFromRedux;

  const dynamicCallbackToRunAfterNewOKRCreation = isObjective ? onObjectiveCreation : onKeyResultCreation;

  const defaultCallbackToRunAfterNewOKRCreation = useCallback(() => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
    queryClient.invalidateQueries([QUERY_KEYS.OKR_CHILDREN]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsInsightsGroupByIndividualData]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsInsightsGroupByGoalsData]);
  }, [queryClient]);

  let callbackToRunAfterNewOKRCreation = useCallback(
    (okrDetails) => {
      dynamicCallbackToRunAfterNewOKRCreation(okrDetails);
      defaultCallbackToRunAfterNewOKRCreation();
    },
    [defaultCallbackToRunAfterNewOKRCreation, dynamicCallbackToRunAfterNewOKRCreation]
  );

  if (typeof dynamicCallbackToRunAfterNewOKRCreation !== 'function') {
    callbackToRunAfterNewOKRCreation = defaultCallbackToRunAfterNewOKRCreation;
  }

  const { mutate, isMutating } = useCreateNewOKRMutation();

  const disableSubmit = validationError?.length > 0 || isMutating;

  const closeAndOpenPanel = useCallback(
    (newOKR, okrPanelType = OKR_TYPES.OBJECTIVE) => {
      const { uuid, type, text, visibilityType, timeBoundDetails, progressType, measure, measureType } = newOKR;

      const payload = {
        type: okrPanelType,
        parentPost: {
          uuid,
          type,
          text,
          visibilityType,
        },
        timeBoundDetails,
        parentProgressType: progressType,
        parentMeasure: measure,
        parentMeasureType: measureType,
      };
      dispatch(openCreateOKRPanel(payload));
    },
    [dispatch]
  );

  const handleSave = useCallback(
    async ({ okrDetails, onSuccess }) => {
      /* Queries to invalidate
      1.1 OKR ( so that parent and timeline component reflects updated )
      1.2 OKR so that progress updates can reflect ( in case of derived progress)
      2. refetch alignment section.
    */
      /*   const okrQueryKey = [QUERY_KEYS.KEY_RESULT, okrUuid];
    const alignmentQueryKey = [QUERY_KEYS.OKR_TOP_HIERARCHY, okrUuid]; */

      mutate(
        {
          okrDetails,
        },
        {
          onError: (err) => {
            console.error(err.message);
            toast({
              title:
                err.message ||
                `The ${
                  isObjective
                    ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
                    : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
                } could not be created`,
              status: TOAST_STATUSES.ERROR,
              position: 'bottom-left',
            });
          },
          onSuccess: (data) => {
            const newlyCreatedOKR = data?.entity;
            /* queryClient.invalidateQueries(okrQueryKey);
            queryClient.invalidateQueries(alignmentQueryKey); */

            // queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);

            toast({
              title: `The new ${
                isObjective
                  ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
                  : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
              } has been successfully created`,
              status: TOAST_STATUSES.SUCCESS,
              position: 'bottom-left',
            });
            if (typeof onSuccess === 'function') {
              onSuccess(newlyCreatedOKR);
            }
          },
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isObjective, keyResultsName, objectivesName, mutate]
  );

  const handleSaveAndAddChildObjective = useCallback(() => {
    const okrDetails = okrDetailsRef?.current;
    const onSuccess = (okrDetails) => {
      callbackToRunAfterNewOKRCreation(okrDetails);
      closeAndOpenPanel(okrDetails, OKR_TYPES.OBJECTIVE);
      sendOpenedObjectivePanelEvent({
        eventProperties: {
          source: isObjective ? 'Add Objective Panel' : 'Add KR Panel',
          page,
          type: 'Add Objective Panel',
        },
      });
    };

    handleSave({ okrDetails, onSuccess });
  }, [callbackToRunAfterNewOKRCreation, closeAndOpenPanel, handleSave, okrDetailsRef]);

  const handleSaveAndAddObjective = useCallback(() => {
    const okrDetails = okrDetailsRef?.current;
    const onSuccess = (okrDetails) => {
      callbackToRunAfterNewOKRCreation(okrDetails);
      closeAndOpenPanel(currentOpenOKRDetails, OKR_TYPES.OBJECTIVE);
      sendOpenedObjectivePanelEvent({
        eventProperties: {
          source: isObjective ? 'Add Objective Panel' : 'Add KR Panel',
          page,
          type: 'Add Objective Panel',
        },
      });
    };

    handleSave({ okrDetails, onSuccess });
  }, [callbackToRunAfterNewOKRCreation, closeAndOpenPanel, currentOpenOKRDetails, handleSave, okrDetailsRef]);

  const handleSaveAndAddChildKeyResult = useCallback(() => {
    const okrDetails = okrDetailsRef?.current;
    const onSuccess = (okrDetails) => {
      callbackToRunAfterNewOKRCreation(okrDetails);
      closeAndOpenPanel(okrDetails, OKR_TYPES.KEY_RESULT);
      sendOpenedKRPanelEvent({
        eventProperties: {
          source: isObjective ? 'Add Objective Panel' : 'Add KR Panel',
          page,
          type: 'Add KR Panel',
        },
      });
    };
    handleSave({ okrDetails, onSuccess });
  }, [callbackToRunAfterNewOKRCreation, closeAndOpenPanel, handleSave, okrDetailsRef]);

  const handleSaveAndAddKeyResult = useCallback(() => {
    const okrDetails = okrDetailsRef?.current;
    const onSuccess = (okrDetails) => {
      callbackToRunAfterNewOKRCreation(okrDetails);
      closeAndOpenPanel(currentOpenOKRDetails, OKR_TYPES.KEY_RESULT);
      sendOpenedKRPanelEvent({
        eventProperties: {
          source: isObjective ? 'Add Objective Panel' : 'Add KR Panel',
          page,
          type: 'Add KR Panel',
        },
      });
    };
    handleSave({ okrDetails, onSuccess });
  }, [callbackToRunAfterNewOKRCreation, closeAndOpenPanel, currentOpenOKRDetails, handleSave, okrDetailsRef]);

  const handleCancelClick = () => {
    dispatch(closeSecondaryPanel());
  };

  const handleSaveAndClose = useCallback(() => {
    const okrDetails = okrDetailsRef?.current;
    const onSuccess = (okrDetails) => {
      callbackToRunAfterNewOKRCreation(okrDetails);

      dispatch(closeSecondaryPanel());
    };

    handleSave({ okrDetails, onSuccess });
  }, [callbackToRunAfterNewOKRCreation, dispatch, handleSave, okrDetailsRef]);

  return (
    <Flex
      flex="auto 0 0"
      borderTopWidth="1px"
      h="72px"
      justifyContent="flex-end"
      alignItems="center"
      backgroundColor={theme.colors.neutral.F5F7F9}
      px={3}
    >
      <Button variant="tertiary" mr="auto" onClick={handleCancelClick}>
        Cancel
      </Button>

      <Box>
        <SaveAndAddComponent
          disabled={disableSubmit}
          disabledReason={validationError}
          isObjective={isObjective}
          handleSaveAndAddChildObjective={handleSaveAndAddChildObjective}
          handleSaveAndAddObjective={handleSaveAndAddObjective}
          handleSaveAndAddChildKeyResult={handleSaveAndAddChildKeyResult}
          handleSaveAndAddKeyResult={handleSaveAndAddKeyResult}
          handleSaveAndExit={handleSaveAndClose}
        />

        {/* <Button
            variant="filled"
            colorScheme="blue"
            onClick={handleSaveAndClose}
            disabled={disableSubmit}
            ml={3}
          >
            Save
          </Button> */}
      </Box>
    </Flex>
  );
};

export default CreateOKRPanelFooter;
